/**
 * personnelDataService — personel / yetki / departman icin SQL-once + Firebase okuma-yedekli adaptor.
 *
 * Backend: gts_hr_api (`/api/hr/personnel`, `/api/hr/permissions`, `/api/hr/departments`) — `hrApi` client
 *          (X-Company-Id + Authorization interceptor'dan otomatik).
 *
 * OKUMA (liste): yalniz SQL. Firebase okuma-yedegi Faz 4'te kaldirildi.
 * YAZMA:        create/update/delete/transfer + tum izin yazimlari DAIMA dogrudan SQL'e gider.
 * IZIN OKUMA:   SQL (tek kaynak).
 *
 * KRITIK: personelin kanonik kimligi Firebase Auth UID'dir → `Personnel.id` DAIMA uid kalir
 *         (SQL integer Id ayrica `dbId` alaninda tasinir).
 */

import { hrApi } from '../../config/apiClient';

export interface Personnel {
  /** Firebase Auth UID — kanonik kimlik (DEGISMEZ). */
  id: string;
  /** SQL integer birincil anahtar (yalniz SQL modunda dolar). */
  dbId?: number;
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  role?: string;
  branchesId?: string | null;
  departmentId?: number | string | null;
  employeeId?: string;
  profileImageUrl?: string;
  isActive?: boolean;
  isDeleted?: boolean;
  // Personel detay (HR) ek alanlari
  jobTitle?: string | null;
  startDate?: string | null;
  birthDate?: string | null;
  address?: string | null;
  emergencyContactName?: string | null;
  emergencyContactPhone?: string | null;
  notes?: string | null;
  [key: string]: unknown;
}

export interface ScreenPermission {
  id?: number;
  firebaseUid: string;
  screenType: string;
  screenId?: string | null;
  isEnabled: boolean;
  settings?: any;
  [key: string]: unknown;
}

export interface Department {
  /** SQL integer Id (SQL modu) veya RTDB push key (fallback). */
  id: number | string;
  name: string;
  description?: string | null;
  head?: string | null;
  branchesId?: string | null;
  legacyFirebaseKey?: string | null;
  [key: string]: unknown;
}

export interface PersonnelCreateInput {
  firebaseUid: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role?: string;
  companyName?: string;
  branchesId?: string | null;
  employeeId?: string;
  profileImageUrl?: string;
  /** Opsiyonel BCrypt hash; verilmezse sunucu "PENDING" yazar. */
  passwordHash?: string;
  // Personel detay (HR) ek alanlari (opsiyonel)
  jobTitle?: string | null;
  startDate?: string | null;
  birthDate?: string | null;
  address?: string | null;
  emergencyContactName?: string | null;
  emergencyContactPhone?: string | null;
  notes?: string | null;
  departmentId?: number | null;
}

export interface PersonnelUpdateInput {
  firstName?: string;
  lastName?: string;
  phone?: string;
  role?: string;
  branchesId?: string | null;
  employeeId?: string;
  profileImageUrl?: string;
  // Personel detay (HR) ek alanlari.
  // KURAL: alan gonderilmezse degismez; metin alanlarinda "" => temizle (null).
  // departmentId: 0 => temizle (null), >0 => ata.
  jobTitle?: string | null;
  startDate?: string | null;
  birthDate?: string | null;
  address?: string | null;
  emergencyContactName?: string | null;
  emergencyContactPhone?: string | null;
  notes?: string | null;
  departmentId?: number | null;
}

export interface PersonnelLimitInfo {
  currentPersonnelCount: number;
  maxPersonnel: number;
  remainingSlots: number;
  limitReached: boolean;
}

export interface DepartmentInput {
  name: string;
  description?: string | null;
  head?: string | null;
  branchesId?: string | null;
  legacyFirebaseKey?: string | null;
}

// ===================== MAP YARDIMCILARI =====================

function mapSqlPersonnel(u: any): Personnel {
  return {
    ...u,
    id: u?.firebaseUid ?? '', // KANONIK = uid
    dbId: u?.id,
    name: u?.name || `${u?.firstName ?? ''} ${u?.lastName ?? ''}`.trim(),
    firstName: u?.firstName,
    lastName: u?.lastName,
    email: u?.email,
    phone: u?.phone,
    role: u?.role,
    branchesId: u?.branchesId ?? null,
    employeeId: u?.employeeId,
    profileImageUrl: u?.profileImageUrl,
    isActive: u?.isActive,
    isDeleted: u?.isDeleted,
  };
}

function mapSqlDepartment(d: any): Department {
  return {
    ...d,
    id: d?.id,
    name: d?.name || '',
    description: d?.description ?? null,
    head: d?.head ?? null,
    branchesId: d?.branchesId ?? null,
    legacyFirebaseKey: d?.legacyFirebaseKey ?? null,
  };
}

// ===================== PERSONEL — OKUMA =====================

/**
 * Sirketin personelini getirir (SADECE SQL). id=uid korunur.
 * SQL ucu sayfali → tum sayfalar toplanir (pageSize=500).
 */
export async function getPersonnel(
  _companyId: string,
  options: { includeDeleted?: boolean } = {}
): Promise<Personnel[]> {
  const includeDeleted = options.includeDeleted ?? false;
  const pageSize = 500;
  let page = 1;
  const all: Personnel[] = [];
  // Guvenli ust sinir: sonsuz donguden kacin.
  for (let guard = 0; guard < 100; guard++) {
    const res = await hrApi.get('/api/hr/personnel', {
      params: { page, pageSize, includeDeleted },
    });
    const items = Array.isArray(res.data?.items) ? res.data.items : [];
    all.push(...items.map(mapSqlPersonnel));
    const total = res.data?.total ?? all.length;
    if (items.length === 0 || all.length >= total) break;
    page++;
  }
  return all;
}

/** Tek personeli FirebaseUid ile getirir (SADECE SQL). */
export async function getPersonnelByUid(uid: string, _companyId?: string): Promise<Personnel | null> {
  const res = await hrApi.get(`/api/hr/personnel/${encodeURIComponent(uid)}`);
  return res.data?.data ? mapSqlPersonnel(res.data.data) : null;
}

/** Sirketin personel limiti durumu (SADECE SQL). */
export async function checkPersonnelLimit(): Promise<PersonnelLimitInfo> {
  const res = await hrApi.post('/api/hr/personnel/check-limit', {});
  return {
    currentPersonnelCount: res.data?.currentPersonnelCount ?? 0,
    maxPersonnel: res.data?.maxPersonnel ?? 0,
    remainingSlots: res.data?.remainingSlots ?? 0,
    limitReached: res.data?.limitReached ?? false,
  };
}

// ===================== PERSONEL — YAZMA (SADECE SQL) =====================

/** Admin tarafindan yeni personel olusturur (Firebase Auth kullanicisi onceden acilmis olmali). */
export async function createPersonnel(input: PersonnelCreateInput): Promise<Personnel> {
  const res = await hrApi.post('/api/hr/personnel/create-by-admin', input);
  if (res.data?.success === false) throw new Error(res.data?.message || 'createPersonnel basarisiz');
  return mapSqlPersonnel(res.data?.data);
}

export interface PersonnelCreateByManagerInput {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone?: string | null;
  branchesId: string;
  departmentId?: string | null;
}

/**
 * Şube yöneticisinin kendi şubesine personel oluşturması (auth credential + SystemUser).
 * Yetki (personnel_create) ve şube kapsamı sunucuda doğrulanır; rol her zaman employee.
 */
export async function createPersonnelByManager(input: PersonnelCreateByManagerInput): Promise<{ userId: string; message?: string }> {
  const res = await hrApi.post('/api/hr/personnel/create-by-manager', {
    FirstName: input.firstName,
    LastName: input.lastName,
    Email: input.email,
    Password: input.password,
    Phone: input.phone ?? null,
    BranchesId: input.branchesId,
    DepartmentId: input.departmentId ?? null,
  });
  if (res.data?.success === false) throw new Error(res.data?.message || 'createPersonnelByManager basarisiz');
  return { userId: res.data?.data?.userId, message: res.data?.message };
}

/** Var olan kullaniciyi sirkete ekler (uid veya employeeId). */
export async function addPersonnel(input: { firebaseUid?: string; employeeId?: string }): Promise<string> {
  const res = await hrApi.post('/api/hr/personnel/add', input);
  if (res.data?.success === false) throw new Error(res.data?.message || 'addPersonnel basarisiz');
  return res.data?.personnelId;
}

/** Toplu personel ekleme. Sunucu sonucunu (success/failed) doner. */
export async function addPersonnelBulk(employeeIds: string[]): Promise<any> {
  const res = await hrApi.post('/api/hr/personnel/add-bulk', { employeeIds });
  if (res.data?.success === false) throw new Error(res.data?.message || 'addPersonnelBulk basarisiz');
  return res.data;
}

/** Kullanicinin kendisini sirkete eklemesi (CompanyId bossa). */
export async function joinPersonnel(uid: string): Promise<string> {
  const res = await hrApi.post('/api/hr/personnel/join', { firebaseUid: uid });
  if (res.data?.success === false) throw new Error(res.data?.message || 'joinPersonnel basarisiz');
  return res.data?.personnelId;
}

/** Personel bilgisi gunceller (yalniz verilen alanlar). */
export async function updatePersonnel(uid: string, input: PersonnelUpdateInput): Promise<Personnel> {
  const res = await hrApi.post('/api/hr/personnel/update', { firebaseUid: uid, ...input });
  if (res.data?.success === false) throw new Error(res.data?.message || 'updatePersonnel basarisiz');
  return mapSqlPersonnel(res.data?.data);
}

/** Sube transferi = update(branchesId). */
export async function transferBranch(uid: string, branchesId: string | null): Promise<Personnel> {
  return updatePersonnel(uid, { branchesId });
}

/** Personeli soft-delete eder (CompanyId restore icin korunur). */
export async function deletePersonnel(uid: string): Promise<void> {
  const res = await hrApi.post('/api/hr/personnel/delete', { firebaseUid: uid });
  if (res.data?.success === false) throw new Error(res.data?.message || 'deletePersonnel basarisiz');
}

/** Soft-deleted personeli geri alir. */
export async function restorePersonnel(uid: string): Promise<void> {
  const res = await hrApi.post('/api/hr/personnel/restore', { firebaseUid: uid });
  if (res.data?.success === false) throw new Error(res.data?.message || 'restorePersonnel basarisiz');
}

/** Kullanici sirketten ayrilir (CompanyId temizlenir). */
export async function leavePersonnel(uid: string): Promise<void> {
  const res = await hrApi.post('/api/hr/personnel/leave', { firebaseUid: uid });
  if (res.data?.success === false) throw new Error(res.data?.message || 'leavePersonnel basarisiz');
}

// ===================== EKRAN YETKILERI (SADECE SQL) =====================

/** Bir kullanicinin TUM ekran yetkileri. */
export async function getScreenPermissions(uid: string): Promise<ScreenPermission[]> {
  const res = await hrApi.get(`/api/hr/personnel/${encodeURIComponent(uid)}/permissions`);
  return Array.isArray(res.data?.items) ? res.data.items : [];
}

/** Bir kullanicinin belirli screenType yetkileri. */
export async function getScreenPermissionsByType(uid: string, screenType: string): Promise<ScreenPermission[]> {
  const res = await hrApi.get(
    `/api/hr/personnel/${encodeURIComponent(uid)}/permissions/${encodeURIComponent(screenType)}`
  );
  return Array.isArray(res.data?.items) ? res.data.items : [];
}

/** Tek yetki satiri upsert. settings opsiyonel (verilmezse mevcut korunur). */
export async function setScreenPermission(
  uid: string,
  screenType: string,
  screenId: string | null,
  isEnabled: boolean,
  settings?: any
): Promise<ScreenPermission> {
  const body: any = { firebaseUid: uid, screenType, screenId, isEnabled };
  if (settings !== undefined) body.settings = settings;
  const res = await hrApi.post('/api/hr/permissions/set', body);
  if (res.data?.success === false) throw new Error(res.data?.message || 'setScreenPermission basarisiz');
  return res.data?.data;
}

/** Coklu yetki satiri upsert (atomik). */
export async function bulkSetScreenPermissions(
  items: Array<{ firebaseUid: string; screenType: string; screenId?: string | null; isEnabled?: boolean; settings?: any }>
): Promise<ScreenPermission[]> {
  const res = await hrApi.post('/api/hr/permissions/bulk-set', { items });
  if (res.data?.success === false) throw new Error(res.data?.message || 'bulkSetScreenPermissions basarisiz');
  return Array.isArray(res.data?.items) ? res.data.items : [];
}

/** Yetki satiri(lari) sil. screenId verilmezse o screenType'in tum satirlari silinir. */
export async function deleteScreenPermission(
  uid: string,
  screenType: string,
  screenId?: string | null
): Promise<number> {
  const res = await hrApi.post('/api/hr/permissions/delete', { firebaseUid: uid, screenType, screenId });
  if (res.data?.success === false) throw new Error(res.data?.message || 'deleteScreenPermission basarisiz');
  return res.data?.deleted ?? 0;
}

// ---- WMS aksiyon yetkileri (wmsScreens/{screenId}) ----

export async function getWmsPermissions(
  screenId: string,
  uid: string
): Promise<{ found: boolean; isEnabled: boolean; settings: any }> {
  const res = await hrApi.get(
    `/api/hr/wms-screens/${encodeURIComponent(screenId)}/personnel-permissions/${encodeURIComponent(uid)}`
  );
  return {
    found: res.data?.found ?? false,
    isEnabled: res.data?.isEnabled ?? false,
    settings: res.data?.settings ?? null,
  };
}

export async function setWmsPermissions(
  screenId: string,
  uid: string,
  isEnabled: boolean,
  settings?: any
): Promise<ScreenPermission> {
  const body: any = { firebaseUid: uid, isEnabled };
  if (settings !== undefined) body.settings = settings;
  const res = await hrApi.post(
    `/api/hr/wms-screens/${encodeURIComponent(screenId)}/personnel-permissions/set`,
    body
  );
  if (res.data?.success === false) throw new Error(res.data?.message || 'setWmsPermissions basarisiz');
  return res.data?.data;
}

// ---- Yonetici paneli (managerPanel — tek satir, modul haritasi SettingsJson) ----

export async function getManagerPanel(uid: string): Promise<{ found: boolean; isEnabled: boolean; settings: any }> {
  const res = await hrApi.get(`/api/hr/personnel/${encodeURIComponent(uid)}/manager-panel`);
  return {
    found: res.data?.found ?? false,
    isEnabled: res.data?.isEnabled ?? false,
    settings: res.data?.settings ?? null,
  };
}

export async function setManagerPanel(uid: string, isEnabled: boolean, settings?: any): Promise<ScreenPermission> {
  const body: any = { firebaseUid: uid, isEnabled };
  if (settings !== undefined) body.settings = settings;
  const res = await hrApi.post('/api/hr/permissions/manager-panel/set', body);
  if (res.data?.success === false) throw new Error(res.data?.message || 'setManagerPanel basarisiz');
  return res.data?.data;
}

// ---- Admin paneli (adminPanel — alt admin/subadmin modul haritasi, managerPanel deseni) ----

export async function getAdminPanel(uid: string): Promise<{ found: boolean; isEnabled: boolean; settings: any }> {
  const res = await hrApi.get(`/api/hr/personnel/${encodeURIComponent(uid)}/admin-panel`);
  return {
    found: res.data?.found ?? false,
    isEnabled: res.data?.isEnabled ?? false,
    settings: res.data?.settings ?? null,
  };
}

/** Alt admin modul haritasini kaydeder. Backend'de yalniz ana admin token'iyla calisir (CompanyAdminOnly). */
export async function setAdminPanel(uid: string, isEnabled: boolean, settings?: any): Promise<ScreenPermission> {
  const body: any = { firebaseUid: uid, isEnabled };
  if (settings !== undefined) body.settings = settings;
  const res = await hrApi.post('/api/hr/permissions/admin-panel/set', body);
  if (res.data?.success === false) throw new Error(res.data?.message || 'setAdminPanel basarisiz');
  return res.data?.data;
}

/** Sirketteki tum adminPanel satirlari (Kullanicilar sayfasi). Yalniz ana admin token'iyla calisir. */
export async function getAdminPanelList(): Promise<ScreenPermission[]> {
  const res = await hrApi.get('/api/hr/permissions/admin-panel/list');
  if (res.data?.success === false) throw new Error(res.data?.message || 'getAdminPanelList basarisiz');
  return Array.isArray(res.data?.items) ? res.data.items : [];
}

// ---- Alt admin islem loglari (SubAdminActivityLogs — Kullanicilar sayfasi "Islem Gecmisi" modali) ----

export interface SubAdminActivityLogItem {
  id: number;
  userName: string | null;
  method: string;
  apiName: string | null;
  path: string;
  actionLabel: string | null;
  pageRoute: string | null;
  statusCode: number | null;
  actionEpochMs: number;
  actionAt: string;
}

export interface SubAdminActivityLogPage {
  total: number;
  page: number;
  pageSize: number;
  items: SubAdminActivityLogItem[];
}

/**
 * Bir alt adminin islem loglari (sayfali, ActionAt DESC).
 * Yalniz ana admin token'iyla calisir (CompanyAdminOnly).
 */
export async function getSubAdminActivityList(
  uid: string,
  page: number,
  pageSize: number,
  from?: string,
  to?: string
): Promise<SubAdminActivityLogPage> {
  const params: Record<string, string | number> = { uid, page, pageSize };
  if (from) params.from = from;
  if (to) params.to = to;
  const res = await hrApi.get('/api/hr/subadmin-activity/list', { params });
  if (res.data?.success === false) throw new Error(res.data?.message || 'getSubAdminActivityList basarisiz');
  return {
    total: res.data?.total ?? 0,
    page: res.data?.page ?? page,
    pageSize: res.data?.pageSize ?? pageSize,
    items: Array.isArray(res.data?.items) ? res.data.items : [],
  };
}

/**
 * Bir sube yoneticisinin (manager) islem loglari (sayfali, ActionAt DESC).
 * SubAdmin listesiyle ayni donus tipi; kaynak tablo ManagerActivityLogs.
 * Yalniz ana admin token'iyla calisir (CompanyAdminOnly).
 */
export async function getManagerActivityList(
  uid: string,
  page: number,
  pageSize: number,
  from?: string,
  to?: string
): Promise<SubAdminActivityLogPage> {
  const params: Record<string, string | number> = { uid, page, pageSize };
  if (from) params.from = from;
  if (to) params.to = to;
  const res = await hrApi.get('/api/hr/manager-activity/list', { params });
  if (res.data?.success === false) throw new Error(res.data?.message || 'getManagerActivityList basarisiz');
  return {
    total: res.data?.total ?? 0,
    page: res.data?.page ?? page,
    pageSize: res.data?.pageSize ?? pageSize,
    items: Array.isArray(res.data?.items) ? res.data.items : [],
  };
}

// ===================== DEPARTMANLAR =====================

/** Sirketin departmanlari (SADECE SQL). */
export async function getDepartments(_companyId: string): Promise<Department[]> {
  const res = await hrApi.get('/api/hr/departments');
  const raw = Array.isArray(res.data?.items) ? res.data.items : [];
  return raw.map(mapSqlDepartment);
}

/** Yeni departman olusturur (SADECE SQL). */
export async function createDepartment(input: DepartmentInput): Promise<Department> {
  const res = await hrApi.post('/api/hr/departments/create', input);
  if (res.data?.success === false) throw new Error(res.data?.message || 'createDepartment basarisiz');
  return mapSqlDepartment(res.data?.data);
}

/** Departman gunceller (SADECE SQL). */
export async function updateDepartment(
  id: number | string,
  input: Partial<DepartmentInput>
): Promise<Department> {
  const res = await hrApi.post('/api/hr/departments/update', { id, ...input });
  if (res.data?.success === false) throw new Error(res.data?.message || 'updateDepartment basarisiz');
  return mapSqlDepartment(res.data?.data);
}

/** Departmani soft-delete eder (SADECE SQL). */
export async function deleteDepartment(id: number | string): Promise<void> {
  const res = await hrApi.post('/api/hr/departments/delete', { id });
  if (res.data?.success === false) throw new Error(res.data?.message || 'deleteDepartment basarisiz');
}
